/**
 * Engine Output — one row per PRIORITY, always, regardless of planning mode.
 *
 * Was: one row per work-unit-grain PlanRecord (plus a placeholder row per
 * priority with none), so Work Units mode showed more rows than priorities
 * existed — e.g. 9 priorities, one decomposed into 4 work units, rendered as
 * 12 rows. Decomposition changes how a priority's demand is COMPUTED, not
 * how many priorities exist; every other engine-output surface
 * (Achievability, Compare, the priority table in Summary) already renders
 * at priority grain via prioritySummaries, which rolls work-unit records up
 * per priority. This page was the one holdout showing the exploded count.
 *
 * Work-unit-level detail isn't gone — it moved from the top-level row list
 * into the detail tray: click a priority to see the individual work units
 * (if any) that were planned underneath it.
 */

import { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import { usePlanningEngine } from './PlanningEngineContext';
import { PlanningEntityListPage } from './PlanningEntityListPage';
import { engineInputKindLabel } from '../../../lib/planningEngineService';
import type {
  DecisionWithParticipation,
  PlanDecision,
  PlanRecord,
  PriorityFeasibility,
} from '../../../lib/planningEngineModule.types';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';
import { planDecisionClass } from './planningEngineViewShared';
import { FEASIBILITY_PLAIN_LANGUAGE, feasibilityTone, type FeasibilityTone } from '../../../lib/planningFeasibilityLanguage';

// ─── Shared badges ────────────────────────────────────────────────────────────

const FEASIBILITY_BADGE_CLASS: Record<FeasibilityTone, string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]',
  amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]',
  red: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]',
  neutral: 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20',
};

function feasibilityBadge(f: PriorityFeasibility) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${FEASIBILITY_BADGE_CLASS[feasibilityTone(f)]}`}>
      {f}
    </span>
  );
}

function decisionBadge(decision: PlanDecision | DecisionWithParticipation) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${planDecisionClass(decision as PlanDecision)}`}>
      {decision}
    </span>
  );
}

// ─── Row (priority grain) ─────────────────────────────────────────────────────

interface PriorityRow {
  id: string;
  priorityName: string;
  displayOrder: number;
  workUnitCount: number;
  requiredWU: number;
  includedWU: number;
  deferredWU: number;
  blockedOrNotReadyWU: number;
  coveragePercent: number;
  feasibility: PriorityFeasibility;
  hasParticipationRisk: boolean;
  decisionWithParticipation: DecisionWithParticipation;
  blockedReason: string | null;
  /** The underlying work-unit-grain records — surfaced in the detail tray, not the list. */
  workUnitRecords: PlanRecord[];
}

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'displayOrder', label: 'Priority order' },
  { field: 'priorityName', label: 'Name' },
  { field: 'coveragePercent', label: 'Coverage' },
];

const FEASIBILITY_OPTIONS: PriorityFeasibility[] = [
  'Fully Achievable',
  'Partially Achievable',
  'Not Achievable',
  'No Work',
];

const DWP_DECISIONS: DecisionWithParticipation[] = [
  'Included',
  'At Risk - Participation',
  'At Risk - Capacity',
  'Blocked - Capability',
  'Out - capacity',
  'Blocked',
  'Not Ready',
  'Needs Sizing',
  'No Capacity Model',
  'No Work Units Defined',
];

const FACETS: FacetDefinition<string>[] = [
  {
    field: 'feasibility',
    label: 'Feasibility',
    type: 'multi',
    options: FEASIBILITY_OPTIONS.map((f) => ({ value: f, label: f })),
  },
  {
    field: 'decisionWithParticipation',
    label: 'With Participation',
    type: 'multi',
    options: DWP_DECISIONS.map((d) => ({ value: d, label: d })),
  },
];

export default function PlanOfRecordTable() {
  const { planOutput, running, error, runEngineForScenario, activeScenario, scopedItemIds, engineInput } = usePlanningEngine();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows: PriorityRow[] = useMemo(() => {
    if (!planOutput) return [];
    const summaries = scopedItemIds
      ? planOutput.prioritySummaries.filter((ps) => scopedItemIds.has(ps.priority.id))
      : planOutput.prioritySummaries;

    const sorted = [...summaries].sort((a, b) => {
      const aOrder = a.priority.forceRank ?? 9999;
      const bOrder = b.priority.forceRank ?? 9999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.priority.name.localeCompare(b.priority.name);
    });

    return sorted.map((ps, i) => ({
      id: ps.priority.id,
      priorityName: ps.priority.name,
      displayOrder: ps.priority.forceRank ?? i + 1,
      workUnitCount: planOutput.planRecords.filter((r) => r.priority?.id === ps.priority.id).length,
      requiredWU: ps.requiredWU,
      includedWU: ps.includedWU,
      deferredWU: ps.deferredWU,
      blockedOrNotReadyWU: ps.blockedOrNotReadyWU,
      coveragePercent: ps.coveragePercent,
      feasibility: ps.feasibility,
      hasParticipationRisk: ps.hasParticipationRisk,
      decisionWithParticipation: ps.decisionWithParticipation,
      blockedReason: ps.blockedReason ?? null,
      workUnitRecords: planOutput.planRecords.filter((r) => r.priority?.id === ps.priority.id),
    }));
  }, [planOutput, scopedItemIds]);

  const tableColumns = useMemo((): TableColumnWithRender<PriorityRow>[] => [
    {
      key: 'displayOrder',
      label: '#',
      sortable: true,
      minWidth: 56,
      render: (r) => <span className="text-xs text-[var(--text-ter)] tabular-nums">{r.displayOrder}</span>,
    },
    {
      key: 'priorityName',
      label: 'Name',
      sortable: true,
      minWidth: 280,
      flex: true,
      render: (r) => (
        <div className="min-w-0 flex items-center gap-2">
          <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] block truncate" title={r.priorityName}>
            {r.priorityName}
          </span>
          {r.workUnitCount > 1 && (
            <span
              className="flex-shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 text-[var(--text-sec)]"
              title={`Decomposed into ${r.workUnitCount} work units — see the detail panel for the breakdown`}
            >
              {r.workUnitCount} WU
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'requiredWU',
      label: 'Required',
      sortable: true,
      align: 'right',
      minWidth: 80,
      render: (r) => <span className="text-sm tabular-nums">{r.requiredWU > 0 ? r.requiredWU.toFixed(1) : '—'}</span>,
    },
    {
      key: 'includedWU',
      label: 'Included',
      sortable: true,
      align: 'right',
      minWidth: 80,
      render: (r) => <span className="text-sm tabular-nums text-[var(--green)] dark:text-[var(--text-ter)]">{r.includedWU > 0 ? r.includedWU.toFixed(1) : '—'}</span>,
    },
    {
      key: 'coveragePercent',
      label: 'Coverage',
      sortable: true,
      align: 'right',
      minWidth: 90,
      render: (r) => <span className="text-sm tabular-nums">{r.requiredWU > 0 ? `${r.coveragePercent}%` : '—'}</span>,
    },
    {
      key: 'feasibility',
      label: 'Feasibility',
      minWidth: 140,
      render: (r) => feasibilityBadge(r.feasibility),
    },
    {
      key: 'decisionWithParticipation',
      label: 'With Participation',
      minWidth: 160,
      render: (r) =>
        r.hasParticipationRisk ? decisionBadge(r.decisionWithParticipation) : <span className="text-xs text-[var(--text-ter)]">—</span>,
    },
  ], []);

  const scopeLabel = scopedItemIds && engineInput.type !== 'live'
    ? ` · Scoped to ${engineInputKindLabel(engineInput.type)}: ${'name' in engineInput ? engineInput.name : ''}`
    : '';

  const subtitle = planOutput
    ? `${rows.length} priorit${rows.length !== 1 ? 'ies' : 'y'} · ${PLANNING_MODE_LABELS[planOutput.planningMode]} · ${new Date(planOutput.generatedAt).toLocaleString()}${scopeLabel}`
    : 'Run the engine to generate plan decisions for the active scenario';

  return (
    <PlanningEntityListPage<PriorityRow, string>
      items={rows}
      getId={(r) => r.id}
      title="Engine Output"
      subtitle={subtitle}
      icon={<FileText className="h-4 w-4 text-[var(--text-ter)]" />}
      iconBgClassName="bg-[var(--slate-dim)]"
      itemLabel="priority"
      searchFields={['priorityName']}
      sortOptions={SORT_OPTIONS}
      defaultSort={{ field: 'displayOrder', direction: 'asc' }}
      facets={FACETS}
      tableColumns={tableColumns}
      renderDetail={(r) => (
        <div className="p-6 space-y-4 h-full overflow-auto text-sm">
          <h3 className="text-base font-semibold">{r.priorityName}</h3>
          <dl className="grid grid-cols-2 gap-3">
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Feasibility</dt>
              <dd className="mt-0.5">{feasibilityBadge(r.feasibility)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Coverage</dt>
              <dd className="tabular-nums">{r.requiredWU > 0 ? `${r.coveragePercent}%` : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Required</dt>
              <dd className="tabular-nums">{r.requiredWU > 0 ? r.requiredWU.toFixed(1) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Included</dt>
              <dd className="tabular-nums text-[var(--green)] dark:text-[var(--text-ter)]">{r.includedWU > 0 ? r.includedWU.toFixed(1) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Deferred</dt>
              <dd className="tabular-nums text-[var(--mustard)] dark:text-[var(--mustard)]">{r.deferredWU > 0 ? r.deferredWU.toFixed(1) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-ter)]">Blocked / Not Ready</dt>
              <dd className="tabular-nums text-[var(--coral)] dark:text-[var(--coral)]">{r.blockedOrNotReadyWU > 0 ? r.blockedOrNotReadyWU.toFixed(1) : '—'}</dd>
            </div>
            {r.hasParticipationRisk && (
              <div className="col-span-2">
                <dt className="text-xs text-[var(--text-ter)]">With Participation</dt>
                <dd className="mt-0.5">{decisionBadge(r.decisionWithParticipation)}</dd>
              </div>
            )}
          </dl>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{FEASIBILITY_PLAIN_LANGUAGE[r.feasibility]}</p>
          {r.blockedReason && (
            <p className="text-xs text-[var(--coral)]">{r.blockedReason}</p>
          )}

          {r.workUnitRecords.length > 0 && (
            <div className="pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
              <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">
                Work units ({r.workUnitRecords.length})
              </p>
              <div className="space-y-2">
                {r.workUnitRecords.map((wr) => (
                  <div key={wr.workUnit.id} className="border border-[var(--rule)] dark:border-[var(--rule)] p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate" title={wr.workUnit.title}>
                        {wr.workUnit.title}
                      </span>
                      {decisionBadge(wr.planDecision)}
                    </div>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
                      {wr.workUnitsRequired.toFixed(1)} WU · {wr.decisionReason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      selectedId={selectedId}
      onSelectionChange={setSelectedId}
      urlParamName="item"
      primaryAction={{
        label: running ? 'Running…' : 'Run Engine',
        onClick: () => void runEngineForScenario(),
        disabled: running || !activeScenario,
      }}
      emptyTitle="No engine output yet"
      emptyDescription="Click Run Engine to evaluate priorities and work units against capacity and dependencies for the active scenario."
      emptyAction={
        activeScenario
          ? { label: 'Run Engine', onClick: () => void runEngineForScenario() }
          : undefined
      }
      error={error}
      activeScenario={activeScenario}
    />
  );
}
